<script lang="ts" setup>
import 'vidstack/player/styles/base.css';
import 'vidstack/player';
import 'vidstack/player/ui';
import 'vidstack/icons';

import { isHLSProvider, type MediaCanPlayEvent, type MediaProviderChangeEvent } from 'vidstack';
import type { MediaPlayerElement } from 'vidstack/elements';
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';

import { PlayerStore } from '@/front/stores/player_store.js';
import { VideoStore } from '@/front/stores/video_store.js';
import { UploadStore } from '@/front/stores/upload_store.js';

const playerStore = PlayerStore();
const videoStore = VideoStore();
const uploadStore = UploadStore();

const $loopPlayer = ref<MediaPlayerElement>();
const $src = shallowRef('');
const domVideo = ref<HTMLVideoElement | null>(null);
const loopPreviewFailed = ref(false);
let disposeHlsInstanceListener: (() => void) | null = null;

const emit = defineEmits(['ended']);

const isIOSWebView = computed(() => {
  const userAgent = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/.test(userAgent);
  return isIOS && playerStore.isWebView;
});

const useNativeLoopVideo = computed(() => isIOSWebView.value);

function normalizeUploadKey(value: unknown) {
  if (value === null || value === undefined) {
    return '';
  }
  const asString = String(value);
  if (asString.length === 0) {
    return '';
  }
  try {
    return decodeURIComponent(asString.replace(/\+/g, '%20'));
  } catch {
    return asString;
  }
}

function guessUploadMimeType(filename: unknown, fallbackType: unknown) {
  const normalizedFallback = typeof fallbackType === 'string' ? fallbackType : '';
  if (normalizedFallback) {
    return normalizedFallback;
  }
  const lower = String(filename || '').toLowerCase();
  if (lower.endsWith('.m3u8')) {
    return 'application/x-mpegURL';
  }
  if (lower.endsWith('.mp4')) {
    return 'video/mp4';
  }
  return 'video/mp4';
}

function buildUploadSource() {
  const localSrc = uploadStore.uploadSource?.src;
  if (!localSrc) {
    return null;
  }
  return {
    src: localSrc,
    type: guessUploadMimeType(
      uploadStore.uploadSettings.key,
      uploadStore.uploadSource?.type || uploadStore.uploadFile?.type
    ),
  };
}

function getVideoSource(video) {
  const source = video?.source || {};
  const videoToken = String(video?.token || '');
  const uploadVideoToken = String(uploadStore.video?.token || '');
  const uploadKey = normalizeUploadKey(uploadStore.uploadSettings.key);
  const sourceKey = normalizeUploadKey(source.key);
  const hasUploadSrc = !!uploadStore.uploadFile && !!uploadStore.uploadSource?.src;
  const keyMatches = !!uploadKey && !!sourceKey && uploadKey === sourceKey;
  const uploadVideoMatchesCurrentVideo =
    !!videoToken &&
    !!uploadVideoToken &&
    videoToken === uploadVideoToken;
  const bunnyIdMatches =
    !!uploadStore.uploadSettings.bunnyId &&
    !!source.bunny_id &&
    uploadStore.uploadSettings.bunnyId === source.bunny_id;
  const bunnyVideoIdMatches =
    !!uploadStore.uploadSettings.bunnyVideoId &&
    !!source.bunny_id &&
    uploadStore.uploadSettings.bunnyVideoId === source.bunny_id;
  const shouldPreferLiveUpload =
    source.type_of === 'upload' &&
    uploadVideoMatchesCurrentVideo;

  const uploadSource = hasUploadSrc && (
    keyMatches ||
    bunnyIdMatches ||
    bunnyVideoIdMatches ||
    shouldPreferLiveUpload
  )
    ? buildUploadSource()
    : null;

  return uploadSource || source.hls || source.mp4 || source.s3 || '';
}

function normalizeSourceValue(value) {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const firstString = value.find((entry) => typeof entry === 'string' && entry.length > 0);
    return firstString || '';
  }
  if (value && typeof value === 'object') {
    if (typeof value.src === 'string') return value.src;
    if (typeof value.url === 'string') return value.url;
  }
  return value == null ? '' : String(value);
}

const normalizedSrc = computed(() => normalizeSourceValue($src.value));
const shouldUseNativeBlobPreview = computed(() => {
  return normalizedSrc.value.startsWith('blob:');
});
const shouldUseNativeLoopVideo = computed(() => {
  return useNativeLoopVideo.value || shouldUseNativeBlobPreview.value;
});

watch(
  () => ({
    sourceId: videoStore.video?.source?.id,
    sourceKey: videoStore.video?.source?.key,
    sourceType: videoStore.video?.source?.type_of,
    sourceBunnyId: videoStore.video?.source?.bunny_id,
    uploadKey: uploadStore.uploadSettings.key,
    uploadBunnyId: uploadStore.uploadSettings.bunnyId,
    uploadBunnyVideoId: uploadStore.uploadSettings.bunnyVideoId,
    uploadSrc: uploadStore.uploadSource?.src,
    hasUploadFile: !!uploadStore.uploadFile,
  }),
  () => {
    loopPreviewFailed.value = false;
    $src.value = getVideoSource(videoStore.video);
    log.info(2, '🌜 LoopingPlayer source selected', {
      src: normalizeSourceValue($src.value),
      useNativeLoopVideo: useNativeLoopVideo.value,
      useNativeBlobPreview: shouldUseNativeBlobPreview.value,
      isUploading: uploadStore.isUploading,
      uploadKey: uploadStore.uploadSettings.key,
      videoKey: videoStore.video?.source?.key,
      videoToken: videoStore.video?.token,
      uploadVideoToken: uploadStore.video?.token,
    });
  },
  { immediate: true }
);

function onProviderChange(event: MediaProviderChangeEvent) {
  if (disposeHlsInstanceListener) {
    disposeHlsInstanceListener();
    disposeHlsInstanceListener = null;
  }
  const provider = event.detail;
  if (isHLSProvider(provider)) {
    provider.config = {
      ...provider.config,
      // Prevent startup bitrate probe from forcing a low-res first fragment.
      testBandwidth: false,
      // Keep adaptive quality enabled after startup.
      startLevel: -1,
    };
    disposeHlsInstanceListener = provider.onInstance((hls: any) => {
      const pinStartupQualityToHighest = () => {
        const levels = Array.isArray(hls?.levels) ? hls.levels : [];
        if (!levels.length) {
          return;
        }
        const highestLevelIndex = levels.length - 1;
        hls.startLevel = highestLevelIndex;
        hls.nextAutoLevel = highestLevelIndex;
        log.info(2,
          '🌜 LoopingPlayer HLS startup quality pinned to highest',
          levels[highestLevelIndex]?.height || 'unknown'
        );
      };

      if (typeof hls?.once === 'function') {
        hls.once('hlsManifestParsed', pinStartupQualityToHighest);
      } else if (typeof hls?.on === 'function') {
        const onManifestParsed = () => {
          pinStartupQualityToHighest();
          if (typeof hls?.off === 'function') {
            hls.off('hlsManifestParsed', onManifestParsed);
          }
        };
        hls.on('hlsManifestParsed', onManifestParsed);
      }
    });
  }
}

onBeforeUnmount(() => {
  if (disposeHlsInstanceListener) {
    disposeHlsInstanceListener();
    disposeHlsInstanceListener = null;
  }
});

function onEnded() {
  emit('ended');
}

function onCanPlay(event: MediaCanPlayEvent) {
  setTimeout(() => {
    domVideo.value = $loopPlayer.value?.querySelector('video') ?? null;
    if (!domVideo.value) {
      return;
    }
    domVideo.value.muted = true;
    domVideo.value.volume = 0;
    domVideo.value.play();
  }, 10);
}

function onTimeUpdate(event: MediaTimeUpdateEvent) {
  const t = Math.floor(event.detail.currentTime);
  if (t > 5 && domVideo.value) {
    domVideo.value.currentTime = 0;
  }
}

const getCrossOrigin = computed(() => {
  const source = normalizedSrc.value;
  if (!source) {
    return 'anonymous';
  }
  if (source.startsWith('blob:')) {
    return false;
  }
  if (source.includes('cloudfront')) {
    return false;
  } else if (source.includes('panopto')) {
    return 'anonymous';
  } else {
    return 'anonymous';
  }
});

const nativeCrossOrigin = computed(() => {
  return getCrossOrigin.value === false ? null : getCrossOrigin.value;
});

const onError = (event) => {
  log.info(2, '🌜 LoopingPlayer onError', {
    message: event?.detail?.message,
    code: event?.detail?.code,
    src: normalizeSourceValue($src.value),
    isUploading: uploadStore.isUploading,
    useNativeBlobPreview: shouldUseNativeBlobPreview.value,
  });
  if (videoStore.video.source.backup_hls && $loopPlayer.value?.src != videoStore.video.source.backup_hls) {
    setTimeout(() => {
      log.info(5, '🌜 LoopingPlayer onError, switching to backup HLS', videoStore.video.source.backup_hls);
      if ($loopPlayer.value) {
        $loopPlayer.value.src = videoStore.video.source.backup_hls;
      }
    }, 50);
  } else if (uploadStore.isUploading && uploadStore.uploadSource?.src) {
    log.info(2, '🌜 LoopingPlayer onError while upload active, keeping preview alive');
  } else {
    loopPreviewFailed.value = true;
    log.info(2, '🌜 LoopingPlayer preview disabled after error');
  }
};

const onNativeCanPlay = () => {
  if (!domVideo.value) {
    return;
  }
  domVideo.value.muted = true;
  domVideo.value.volume = 0;
  domVideo.value.play();
};

const onNativeTimeUpdate = () => {
  if (!domVideo.value) {
    return;
  }
  const t = Math.floor(domVideo.value.currentTime);
  if (t > 5) {
    domVideo.value.currentTime = 0;
  }
};

const onNativeError = () => {
  log.info(2, '🌜 LoopingPlayer native onError', {
    src: normalizeSourceValue($src.value),
    isUploading: uploadStore.isUploading,
  });
  if (videoStore.video.source.backup_hls && domVideo.value && domVideo.value.src != videoStore.video.source.backup_hls) {
    domVideo.value.src = videoStore.video.source.backup_hls;
    domVideo.value.load();
    domVideo.value.play();
  } else if (uploadStore.isUploading && uploadStore.uploadSource?.src) {
    log.info(2, '🌜 LoopingPlayer native onError while upload active, keeping preview alive');
  } else {
    loopPreviewFailed.value = true;
    log.info(2, '🌜 LoopingPlayer native preview disabled after error');
  }
};

const loadStrategy = computed(() => {
  if (playerStore.isMobile && !playerStore.isWebView) {
    return 'idle';
  }
  return 'eager';
});
</script>

<template>
  <div class="loop-player-container">
    <div v-if="loopPreviewFailed" class="loop-player-fallback"></div>
    <video
      v-else-if="shouldUseNativeLoopVideo"
      ref="domVideo"
      class="loop-player-video"
      :class="{ 'cover-mode': videoStore.video?.design?.cover_mode }"
      :src="normalizedSrc"
      :crossorigin="nativeCrossOrigin"
      muted
      playsinline
      autoplay
      loop
      preload="auto"
      @ended="onEnded"
      @canplay="onNativeCanPlay"
      @timeupdate="onNativeTimeUpdate"
      @error="onNativeError"
    />

    <media-player
      v-else
      :load="loadStrategy"
      class="loop-player w-full h-full bg-black text-white font-sans overflow-hidden ring-media-focus data-[focus]:ring-4"
      :class="{ 'cover-mode': videoStore.video?.design?.cover_mode }"
      :src="$src"
      :crossOrigin="getCrossOrigin"
      playsInline
      loop
      :muted="true"
      @ended="onEnded"
      @provider-change="onProviderChange"
      @time-update="onTimeUpdate"
      @error="onError"
      @can-play="onCanPlay"
      ref="$loopPlayer"
    >
      <media-provider class="loop-media-provider">
      </media-provider>
    </media-player>
  </div>
</template>

<style>
.loop-player-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.loop-player,
.loop-player .loop-media-provider,
.loop-player-video {
  width: 100%;
  height: 100%;
}

.loop-player-fallback {
  width: 100%;
  height: 100%;
  background: black;
}

.loop-player video,
.loop-player-video {
  object-fit: contain;
  object-position: center center;
  background: black;
}

.loop-player.cover-mode video,
.loop-player-video.cover-mode {
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
}
</style>
